import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["submit", "messages"]

  connect () {
    // remember original button content
    if (this.hasSubmitTarget) {
      this.submitOriginal = this.submitTarget.innerHTML
    }
    this.beforeSendHandler = this.beforeSend.bind(this)
    this.successHandler = this.success.bind(this)
    this.errorHandler = this.error.bind(this)
    this.completeHandler = this.complete.bind(this)

    this.element.addEventListener('ajax:beforeSend', this.beforeSendHandler)
    this.element.addEventListener('ajax:send', this.beforeSendHandler)
    this.element.addEventListener('ajax:success', this.successHandler)
    this.element.addEventListener('ajax:error', this.errorHandler)
    this.element.addEventListener('ajax:complete', this.completeHandler)
  }

  disconnect() {
    this.element.removeEventListener('ajax:beforeSend', this.beforeSendHandler)
    this.element.removeEventListener('ajax:send', this.beforeSendHandler)
    this.element.removeEventListener('ajax:success', this.successHandler)
    this.element.removeEventListener('ajax:error', this.errorHandler)
    this.element.removeEventListener('ajax:complete', this.completeHandler)
  }

  beforeSend () {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Enviando...'
    }
    this.clearMessages()
  }

  success (event) {
    // event.detail usually contains [data, status, xhr]
    let data = event.detail && event.detail[0]
    // If server returns JSON with errors
    if (data && data.errors) {
      this.showErrors(data.errors)
      return
    }

    // If server returned HTML or plain success, show a success message and optionally reset
    this.showSuccess()
    try {
      this.element.reset()
    } catch (e) {}
  }

  error (event) {
    // Try to pull errors from XHR response
    let data = null
    if (event.detail && event.detail.length >= 3) {
      const xhr = event.detail[2]
      try {
        data = JSON.parse(xhr.responseText)
      } catch (e) {
        // not JSON — could be HTML or plain text
        data = xhr.responseText
      }
    }
    if (data && data.errors) {
      this.showErrors(data.errors)
    } else if (event.detail && event.detail[0]) {
      // fallback: try to parse event.detail[0] (may be HTML/text)
      const fallback = event.detail[0]
      this.showErrors(fallback)
    } else {
      this.showErrors(["Ocorreu um erro ao enviar. Tente novamente."])
    }
  }

  complete () {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.innerHTML = this.submitOriginal || 'Enviar'
    }
  }

  showErrors (errors) {
    if (!this.hasMessagesTarget) return
    const container = this.messagesTarget
    container.innerHTML = ''

    // Helper to sanitize and parse HTML responses
    const parseResponseHTML = (htmlString) => {
      if (!htmlString) return ''
      // remove script tags for safety
      let sanitized = String(htmlString).replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')

      // Unescape common JS-escaped sequences like "\n" and "\/" which can appear when server
      // injects HTML inside JS strings. Replace escaped slashes so tags parse correctly.
      sanitized = sanitized.replace(/\\n/g, ' ')
      sanitized = sanitized.replace(/\\r/g, ' ')
      sanitized = sanitized.replace(/\\\//g, '/')
      sanitized = sanitized.replace(/\\"/g, '"')
      sanitized = sanitized.replace(/\\'/g, "'")

      // If server returned JS that assigns HTML to a variable (e.g. "var formulario = ... '<div>..'</div>';")
      // strip any leading characters before the first '<' to get the HTML fragment.
      const firstTag = sanitized.indexOf('<')
      if (firstTag > 0) {
        sanitized = sanitized.slice(firstTag)
      }

      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(sanitized, 'text/html')
        // prefer explicit alert blocks or lists
        const alert = doc.querySelector('.alert') || doc.querySelector('ul') || doc.body
        if (!alert) return sanitized.trim()
        const ul = alert.querySelector('ul')
        if (ul) {
          return Array.from(ul.querySelectorAll('li')).map(li => li.textContent.trim()).filter(Boolean)
        }
        // return trimmed textContent when available
        const text = alert.textContent && alert.textContent.trim()
        if (text && text.length > 0) return [text]
        return alert.innerHTML.trim()
      } catch (e) {
        return sanitized.trim()
      }
    }

    // If errors is an array, display each item; if string, try to parse HTML and extract lis
    if (Array.isArray(errors)) {
      const ul = document.createElement('ul')
      ul.className = 'mb-0'
      errors.forEach(err => {
        const li = document.createElement('li')
        li.textContent = String(err)
        ul.appendChild(li)
      })
      container.className = 'alert alert-danger'
      container.appendChild(ul)
      return
    }

    // If errors is an object with message(s)
    if (errors && typeof errors === 'object') {
      // try to extract values
      const arr = [].concat(...Object.values(errors)).map(String)
      this.showErrors(arr)
      return
    }

    // If it's a string, attempt to parse HTML
    if (typeof errors === 'string') {
      const parsed = parseResponseHTML(errors)
      if (Array.isArray(parsed)) {
        const ul = document.createElement('ul')
        ul.className = 'mb-0'
        parsed.forEach(err => {
          const li = document.createElement('li')
          li.textContent = err
          ul.appendChild(li)
        })
        container.className = 'alert alert-danger'
        container.appendChild(ul)
        return
      }

      // If parsed is HTML string (no li found), set as innerHTML but strip scripts
      container.className = 'alert alert-danger'
      // remove any <script> tags just in case
      const safe = parsed.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      // If safe contains list or divs, use innerHTML, otherwise textContent
      if (/[<>]/.test(safe)) {
        container.innerHTML = safe
      } else {
        const ul = document.createElement('ul')
        ul.className = 'mb-0'
        const li = document.createElement('li')
        li.textContent = safe
        ul.appendChild(li)
        container.appendChild(ul)
      }
      return
    }

    // fallback
    container.className = 'alert alert-danger'
    container.textContent = 'Ocorreu um erro ao enviar. Tente novamente.'
  }

  showSuccess () {
    if (!this.hasMessagesTarget) return
    const container = this.messagesTarget
    container.className = 'alert alert-success'
    container.innerHTML = 'Mensagem enviada com sucesso.'
  }

  clearMessages () {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.innerHTML = ''
    this.messagesTarget.className = ''
  }
}
